import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

const STORAGE_KEY = "tbf_customer_session";

export interface CustomerSession {
  order_id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
}

interface CustomerAuthCtx {
  session: CustomerSession | null;
  login: (s: CustomerSession) => void;
  logout: () => void;
  isLoading: boolean;
}

const Ctx = createContext<CustomerAuthCtx>({
  session: null,
  login: () => {},
  logout: () => {},
  isLoading: true,
});

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CustomerSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSession(JSON.parse(raw) as CustomerSession);
    } catch { /* ignore */ }
    setIsLoading(false);
  }, []);

  const login = (s: CustomerSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSession(s);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  };

  return <Ctx.Provider value={{ session, login, logout, isLoading }}>{children}</Ctx.Provider>;
}

export function useCustomerAuth() {
  return useContext(Ctx);
}
